import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable, of } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { environment } from "environments/environment";
import {
  Role,
  CreateRole,
  UpdateRole,
  Permission,
  PermissionGroup,
  AssignPermissions,
  UserPermissions,
} from "./models/role.model";

@Injectable({
  providedIn: "root",
})
export class RoleService {
  private apiUrl = `${environment.apiUrl}/api`;

  // BehaviorSubject pour les permissions de l'utilisateur courant
  private permissionsSubject = new BehaviorSubject<string[]>([]);
  public permissions$ = this.permissionsSubject.asObservable();

  // BehaviorSubject pour les informations de rôle
  private userRoleSubject = new BehaviorSubject<UserPermissions | null>(null);
  public userRole$ = this.userRoleSubject.asObservable();

  // Cache des permissions
  private permissionsLoaded = false;

  constructor(private http: HttpClient) {
    // Initialiser depuis le localStorage au démarrage
    this.initFromLocalStorage();
  }

  /**
   * Initialise les permissions depuis le localStorage (pour les rafraîchissements de page)
   */
  private initFromLocalStorage(): void {
    const storedPermissions = localStorage.getItem("currentPermissions");
    console.log(
      "🔍 Initialisation des permissions depuis localStorage:",
      storedPermissions ? "trouvé" : "non trouvé"
    );

    if (storedPermissions) {
      try {
        const userPermissions: UserPermissions = JSON.parse(storedPermissions);
        console.log("📦 Données parsées:", userPermissions);

        if (userPermissions && userPermissions.permissions) {
          this.permissionsSubject.next(userPermissions.permissions);
          this.userRoleSubject.next(userPermissions);
          this.permissionsLoaded = true;
          console.log(
            `🔄 Permissions restaurées depuis localStorage pour le rôle "${userPermissions.roleName}":`,
            userPermissions.permissions.length,
            userPermissions.permissions
          );
        } else {
          console.warn("⚠️ userPermissions.permissions est vide ou undefined");
        }
      } catch (e) {
        console.error("Error parsing stored permissions in RoleService", e);
      }
    }
  }

  // ==================== GESTION DES PERMISSIONS ====================

  /**
   * Charge les permissions de l'utilisateur courant
   * Cette méthode doit être appelée après le login
   */
  loadUserPermissions(): Observable<UserPermissions> {
    return this.http.get<UserPermissions>(`${this.apiUrl}/permissions/me`).pipe(
      tap((userPermissions) => {
        this.permissionsSubject.next(userPermissions.permissions);
        this.userRoleSubject.next(userPermissions);
        this.permissionsLoaded = true;

        // Sauvegarder dans le localStorage pour persister après rafraîchissement
        localStorage.setItem("currentPermissions", JSON.stringify(userPermissions));

        console.log(
          `✅ Permissions chargées pour le rôle "${userPermissions.roleName}":`,
          userPermissions.permissions.length
        );
      }),
      catchError((error) => {
        console.error("Erreur lors du chargement des permissions:", error);
        this.permissionsSubject.next([]);
        this.userRoleSubject.next(null);
        return of({
          userId: "",
          tenantId: "",
          permissions: [],
        } as UserPermissions);
      })
    );
  }

  /**
   * Recharge les permissions (après un changement de rôle par exemple)
   */
  refreshPermissions(): Observable<UserPermissions> {
    this.permissionsLoaded = false;
    return this.loadUserPermissions();
  }

  /**
   * Efface les permissions (au logout)
   */
  clearPermissions(): void {
    this.permissionsSubject.next([]);
    this.userRoleSubject.next(null);
    this.permissionsLoaded = false;
    localStorage.removeItem("currentPermissions");
  }

  /**
   * Vérifie si l'utilisateur a une permission spécifique
   */
  hasPermission(permissionCode: string): boolean {
    return this.permissionsSubject.value.includes(permissionCode);
  }

  /**
   * Vérifie si l'utilisateur a au moins une des permissions spécifiées
   */
  hasAnyPermission(permissionCodes: string[]): boolean {
    const currentPermissions = this.permissionsSubject.value;
    return permissionCodes.some((code) => currentPermissions.includes(code));
  }

  /**
   * Vérifie si l'utilisateur a toutes les permissions spécifiées
   */
  hasAllPermissions(permissionCodes: string[]): boolean {
    const currentPermissions = this.permissionsSubject.value;
    return permissionCodes.every((code) => currentPermissions.includes(code));
  }

  /**
   * Vérifie si les permissions sont chargées
   */
  arePermissionsLoaded(): boolean {
    return this.permissionsLoaded;
  }

  /**
   * Récupère le nom du rôle courant
   */
  getCurrentRoleName(): string | null {
    return this.userRoleSubject.value?.roleName || null;
  }

  /**
   * Récupère les permissions courantes
   */
  getCurrentPermissions(): string[] {
    return this.permissionsSubject.value;
  }

  /**
   * Détermine la meilleure page de redirection basée sur les permissions de l'utilisateur
   * Retourne la première route accessible ou /utrav/settings comme fallback
   */
  getDefaultRouteForUser(): string {
    const permissions = this.permissionsSubject.value;

    // Ordre de priorité des pages à vérifier
    const routePermissions = [
      { route: "/utrav/home/statistics", permission: "dashboard.read" },
      { route: "/utrav/appointment", permission: "appointments.read" },
      { route: "/utrav/trips", permission: "trips.read" },
      { route: "/utrav/booking", permission: "bookings.read" },
      { route: "/utrav/review", permission: "reviews.read" },
      { route: "/utrav/push", permission: "notifications.read" },
      { route: "/utrav/loyalty", permission: "loyalty.read" },
      { route: "/utrav/tenants", permission: "tenants.read" },
    ];

    // Trouver la première route accessible
    for (const rp of routePermissions) {
      if (permissions.includes(rp.permission)) {
        console.log(`🎯 Route par défaut trouvée: ${rp.route} (permission: ${rp.permission})`);
        return rp.route;
      }
    }

    // Fallback vers settings qui est accessible à tous les utilisateurs connectés
    console.log("🎯 Fallback vers /utrav/settings");
    return "/utrav/settings";
  }

  // ==================== CRUD DES RÔLES ====================

  /**
   * Récupère tous les rôles du tenant courant
   */
  getRoles(): Observable<Role[]> {
    return this.http.get<Role[]>(`${this.apiUrl}/roles`);
  }

  /**
   * Récupère un rôle par son ID
   */
  getRole(id: string): Observable<Role> {
    return this.http.get<Role>(`${this.apiUrl}/roles/${id}`);
  }

  /**
   * Crée un nouveau rôle
   */
  createRole(role: CreateRole): Observable<Role> {
    return this.http.post<Role>(`${this.apiUrl}/roles`, role);
  }

  /**
   * Met à jour un rôle existant
   */
  updateRole(id: string, role: UpdateRole): Observable<Role> {
    return this.http.put<Role>(`${this.apiUrl}/roles/${id}`, role);
  }

  /**
   * Supprime un rôle
   */
  deleteRole(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/roles/${id}`);
  }

  /**
   * Assigne des permissions à un rôle
   */
  assignPermissions(roleId: string, permissions: AssignPermissions): Observable<Role> {
    return this.http.put<Role>(`${this.apiUrl}/roles/${roleId}/permissions`, permissions);
  }

  /**
   * Assigne un rôle à un utilisateur
   */
  assignRoleToUser(userId: string, roleId: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/roles/assign`, { userId, roleId });
  }

  /**
   * Récupère un rôle par son index legacy
   */
  getRoleByLegacyIndex(legacyIndex: number): Observable<Role> {
    return this.http.get<Role>(`${this.apiUrl}/roles/legacy/${legacyIndex}`);
  }

  // ==================== GESTION DES PERMISSIONS (API) ====================

  /**
   * Récupère toutes les permissions disponibles
   */
  getAllPermissions(): Observable<Permission[]> {
    return this.http.get<Permission[]>(`${this.apiUrl}/permissions`);
  }

  /**
   * Récupère les permissions groupées par module
   */
  getPermissionsGrouped(): Observable<PermissionGroup[]> {
    return this.http.get<PermissionGroup[]>(`${this.apiUrl}/permissions/grouped`);
  }

  /**
   * Récupère les permissions d'un utilisateur spécifique
   */
  getUserPermissions(userId: string): Observable<UserPermissions> {
    return this.http.get<UserPermissions>(`${this.apiUrl}/permissions/user/${userId}`);
  }

  /**
   * Vérifie une permission côté serveur
   */
  checkPermission(permissionCode: string): Observable<{ hasPermission: boolean }> {
    return this.http.get<{ hasPermission: boolean }>(
      `${this.apiUrl}/permissions/check/${permissionCode}`
    );
  }

  /**
   * Vérifie plusieurs permissions côté serveur
   */
  checkPermissions(permissionCodes: string[]): Observable<{
    results: { [key: string]: boolean };
    hasAny: boolean;
    hasAll: boolean;
  }> {
    return this.http.post<any>(`${this.apiUrl}/permissions/check`, {
      permissionCodes,
    });
  }

  // ==================== IMPORT DE RÔLES ====================

  /**
   * Récupère les rôles d'un tenant spécifique (pour import)
   */
  getRolesByTenant(tenantId: string): Observable<Role[]> {
    return this.http.get<Role[]>(`${this.apiUrl}/roles/tenant/${tenantId}`);
  }

  /**
   * Importe les rôles d'un autre tenant vers le tenant courant
   */
  importRolesFromTenant(
    sourceTenantId: string,
    roleIds?: string[]
  ): Observable<{
    message: string;
    data: Role[];
    skippedRoles: string[];
    totalImported: number;
    totalSkipped: number;
  }> {
    const body = roleIds ? { roleIds } : {};
    return this.http.post<{
      message: string;
      data: Role[];
      skippedRoles: string[];
      totalImported: number;
      totalSkipped: number;
    }>(`${this.apiUrl}/roles/import/${sourceTenantId}`, body);
  }

  // ==================== GESTION DES RÔLES GLOBAUX ====================

  /**
   * Récupère tous les rôles globaux (TenantId = null)
   */
  getGlobalRoles(): Observable<Role[]> {
    return this.http.get<Role[]>(`${this.apiUrl}/roles/global`);
  }

  /**
   * Crée un nouveau rôle global
   */
  createGlobalRole(role: CreateRole): Observable<Role> {
    return this.http.post<Role>(`${this.apiUrl}/roles/global`, role);
  }

  /**
   * Met à jour un rôle global
   */
  updateGlobalRole(roleId: string, role: UpdateRole): Observable<Role> {
    return this.http.put<Role>(`${this.apiUrl}/roles/global/${roleId}`, role);
  }

  /**
   * Supprime un rôle global
   */
  deleteGlobalRole(roleId: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.apiUrl}/roles/global/${roleId}`);
  }

  /**
   * Assigne des permissions à un rôle global
   */
  assignPermissionsToGlobalRole(
    roleId: string,
    permissionIds: string[]
  ): Observable<{ message: string }> {
    return this.http.put<{ message: string }>(`${this.apiUrl}/roles/global/${roleId}/permissions`, {
      permissionIds,
    });
  }

  // ==================== HELPERS POUR LA RÉTROCOMPATIBILITÉ ====================

  /**
   * Mapping des anciens rôles (index) vers les codes de permission
   * Utile pendant la période de migration
   */
  private legacyRolePermissionsMap: { [key: number]: string[] } = {
    1: ["*"], // Admin - toutes les permissions
    2: [
      // Manager
      "dashboard.read",
      "trips.read",
      "trips.create",
      "trips.update",
      "bookings.read",
      "bookings.create",
      "bookings.update",
      "payments.read",
      "payments.update",
      "users.read",
      "users.create",
      "users.update",
      "loyalty.read",
      "loyalty.update",
      "reviews.read",
      "reviews.update",
      "appointments.read",
      "appointments.create",
      "appointments.update",
      "settings.read",
      "notifications.read",
      "notifications.create",
      "reports.read",
      "reports.export",
    ],
    3: [
      // Guide
      "trips.read",
      "bookings.read",
      "appointments.read",
      "appointments.create",
      "appointments.update",
      "reviews.read",
      "settings.read",
    ],
    4: [
      // Référent
      "trips.read",
      "bookings.read",
      "users.read",
      "settings.read",
    ],
    5: [
      // Utilisateur
      "trips.read",
      "settings.read",
    ],
  };

  /**
   * Vérifie si un ancien rôle (index) a une permission
   * Utilisé pour la rétrocompatibilité pendant la migration
   */
  legacyRoleHasPermission(roleIndex: number, permissionCode: string): boolean {
    const permissions = this.legacyRolePermissionsMap[roleIndex];
    if (!permissions) return false;
    if (permissions.includes("*")) return true;
    return permissions.includes(permissionCode);
  }
}
